import { useFormContext } from 'react-hook-form';

import type { FormInputProps } from '../types';
import type { FieldValues } from 'react-hook-form';

import { Input } from '@/components';
import { Field, FieldDescription, FieldError } from '@/components/ui/field';
import { FormField } from '@/components/ui/form';

export const FormInput = <TFieldValues extends FieldValues = FieldValues>(
  props: FormInputProps<TFieldValues>,
) => {
  const {
    name,
    label,
    description,
    isRequired,
    type = 'text',
    autoFocus,
    disabled,
    shouldFloatLabel,
    formatOnChange,
    inputProps,
    control,
  } = props;
  const formContext = useFormContext<TFieldValues>();
  const formControl = control || formContext.control;

  const fieldId = `form-field-${String(name)}`;

  return (
    <FormField
      control={formControl}
      name={name}
      render={({ field, fieldState }) => {
        const fieldLabel = (
          <>
            {label}
            {isRequired && (
              <span
                className={
                  fieldState.invalid
                    ? 'text-destructive'
                    : 'text-muted-foreground'
                }
              >
                *
              </span>
            )}
          </>
        );

        return (
          <Field data-invalid={fieldState.invalid}>
            <Input
              {...field}
              id={fieldId}
              type={type}
              label={fieldLabel}
              autoFocus={autoFocus}
              disabled={disabled}
              shouldFloatLabel={shouldFloatLabel}
              isInvalid={fieldState.invalid}
              aria-invalid={fieldState.invalid}
              value={field.value || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (formatOnChange) {
                  const formatted = formatOnChange(value);
                  field.onChange(formatted);
                } else {
                  field.onChange(value);
                }
              }}
              {...inputProps}
            />
            {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            {description && <FieldDescription>{description}</FieldDescription>}
          </Field>
        );
      }}
    />
  );
};
